import template from './ko-field-table-builder.html';
import FieldModel from '../../lib/models/FieldModel.js';

/**
 * Field Table Builder Component
 * Component for managing field tables
 */
class FieldTableBuilderViewModel
{
	constructor(params)
	{
		this.params = params || {};

		this.show_schema_selector = ko.observable(this.params.show_schema_selector !== false);
		this.show_add_button = ko.observable(this.params.show_add_button !== false);
		this.show_edit_button = ko.observable(this.params.show_edit_button !== false);
		this.show_delete_button = ko.observable(this.params.show_delete_button !== false);
		this.show_move_buttons = ko.observable(this.params.show_move_buttons !== false);
		this.show_field_buttons = ko.observable(this.params.show_field_buttons !== false);
		this.display_mode = ko.observable(this.params.display_mode || 'fields');
		this.default_schema = ko.observable(this.params.default_schema || null);

		// Callback functions for external save handling
		this.on_save = this.params.on_save || null;
		this.on_changes = this.params.on_changes || null;
		this.external_field_tables = this.params.field_tables;
		this.field_table_uuids = this.params.field_table_uuids;

		this.schemas = ko.observableArray([]);
		this.selected_schema = ko.observable();
		this.filtered_tables = ko.observableArray();
		this.is_updating_data = false;
		this._uuid_subscribed = false;
		this._external_subscribed = false;

		// Change tracking for external save
		this.pending_changes = {
			tables_to_create: [],
			tables_to_update: [],
			tables_to_delete: [],
			fields_to_create: [],
			fields_to_update: [],
			fields_to_delete: [],
			table_moves: [],
			field_moves: []
		};

		this.selected_schema.subscribe((val) => {
			if (!this.is_updating_data && val)
			{
				const storageKey = this.params?.storageKey || 'custom_tables_selected_schema';
				localStorage.setItem(storageKey, val.schema);
			}
			this.load_schema_table_data();
		});

		// Initialize
		this.init();
	}

	async init()
	{
		await this.updateData();

		// Ensure field tables are loaded when using UUID or external table mode,
		// even if no schema was previously saved in localStorage
		if ((this.field_table_uuids && ko.isObservable(this.field_table_uuids)) ||
			(this.external_field_tables && ko.isObservable(this.external_field_tables)))
		{
			this.load_schema_table_data();
		}
	}

	async updateData()
	{
		this.is_updating_data = true;
		try
		{
			let result = await Grape.fetches.getJSON('/api/record', {
				schema: 'fields',
				table: 'v_fields_schema',
				sortfield: 'schema'
			});

			if (result.status == 'OK')
			{
				const storageKey = this.params?.storageKey || 'custom_tables_selected_schema';
				const saved_schema = localStorage.getItem(storageKey);
				this.schemas(result.records);

				if (saved_schema)
				{
					const matchingSchema = result.records.find(s => s.schema === saved_schema);
					if (matchingSchema)
						this.selected_schema(matchingSchema);
				}
			}
		} finally
		{
			this.is_updating_data = false;
		}
	}

	async load_schema_table_data()
	{
		// If field table UUIDs are provided, load them directly
		if (this.field_table_uuids && ko.isObservable(this.field_table_uuids))
		{
			if (!this._uuid_subscribed)
			{
				this._uuid_subscribed = true;
				this.field_table_uuids.subscribe((uuids) => {
					this.load_field_tables_by_uuids(uuids);
				});
			}

			this.load_field_tables_by_uuids(this.field_table_uuids());
			return;
		}

		// If external field tables are provided, use them instead of loading from schema
		if (this.external_field_tables && ko.isObservable(this.external_field_tables))
		{
			if (!this._external_subscribed)
			{
				this._external_subscribed = true;
				this.external_field_tables.subscribe((tables) => {
					this.load_external_field_tables(tables);
				});
			}

			this.load_external_field_tables(this.external_field_tables());
			return;
		}

		if (!this.selected_schema())
		{
			this.filtered_tables([]);
			return;
		}
		
		let schema = this.selected_schema().tables;
		if (!schema || !Array.isArray(schema))
		{
			this.filtered_tables([]);
			return;
		}

		schema = schema.sort((a, b) => a.idx - b.idx);

		let promises = schema.map(async t => {
			try {
				const FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
				let tbl = new FieldsTableModel({ uuid: t.uuid });
				await tbl.fetch();
				return tbl;
			} catch (error) {
				console.error('Error fetching table:', error);
				return null;
			}
		});

		let tables = await Promise.all(promises);

		tables = tables.filter(t => t !== null);
		this.filtered_tables(tables);
	}

	async load_field_tables_by_uuids(uuids)
	{
		if (!uuids || !Array.isArray(uuids) || uuids.length === 0)
		{
			this.filtered_tables([]);
			return;
		}

		let res = await Grape.fetches.getJSON('/api/record', {
			schema: 'fields',
			table: 'v_fields_table',
			filter: [{ field: 'fields_table_uuid', op: 'in', value: uuids }],
			sortfield: 'table_idx'
		});

		if (res?.status !== 'OK' || !res.records)
		{
			this.filtered_tables([]);
			return;
		}

		// Convert to FieldsTableModel instances and fetch their fields
		let promises = res.records.map(async table => {
			try {
				const FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
				let tbl = new FieldsTableModel(table);
				await tbl.fetch();
				return tbl;
			} catch (error) {
				console.error('Error creating table model:', error);
				return null;
			}
		});

		let tableModels = await Promise.all(promises);
		tableModels = tableModels.filter(t => t !== null);
		this.filtered_tables(tableModels);
	}

	// Load field tables from external source
	async load_external_field_tables(tables)
	{
		if (!tables || !Array.isArray(tables))
		{
			this.filtered_tables([]);
			return;
		}

		// external table data to FieldsTableModel and fetch their fields
		let promises = tables.map(async table => {
			try {
				const FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
				let tbl = new FieldsTableModel(table);
				await tbl.fetch();
				return tbl;
			} catch (error) {
				console.error('Error creating table model:', error);
				return null;
			}
		});

		let tableModels = await Promise.all(promises);
		tableModels = tableModels.filter(t => t !== null);
		this.filtered_tables(tableModels);
	}

	/* add new table */
	async btn_upsert_field_table()
	{
		let filtered_tables = this.filtered_tables() || [];
		let last_table = filtered_tables.length > 0
			? filtered_tables[filtered_tables.length - 1]
			: null;

		let last_table_idx = last_table ? last_table.idx() : -1;
		let dialogParams = {
			fieldsTable: {},
			last_table_idx: last_table_idx,
			save: false
		};

		// If the schema selector is visible and a schema is selected, pass it as default (read-only in dialog)
		if (this.show_schema_selector() && this.selected_schema())
			dialogParams.schema = this.selected_schema().schema;

		if (this.default_schema())
			dialogParams.schema = this.default_schema();

		let res = await Grape.dialog.open('AddEditFieldsTable', dialogParams);

		if (res && res.status === 'OK')
		{
			// Create a temporary UUID for the new table
			const tempUuid = 'temp_' + Date.now();

			// Add to pending changes with the temporary UUID
			const table = { ...res.fieldsTable, fields_table_uuid: tempUuid };
			this.pending_changes.tables_to_create.push(table);

			// Create a temporary model for display
			const FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
			let temp_table = new FieldsTableModel(table);
			this.filtered_tables.push(temp_table);

			// Notify parent of changes
			this.notify_changes();
		}
	}

	/* new field */
	async btn_upsert_field(data)
	{
		let schema = ko.toJS(data).schema || '';
		let table = ko.toJS(data).table || '';
		let fields_table_uuid = ko.toJS(data).fields_table_uuid || '';

		if (schema && table)
		{
			let filtered_tables = this.filtered_tables() || [];
			let selected_table = filtered_tables.find(t => ko.unwrap(t.fields_table_uuid) === fields_table_uuid);
			let last_field = selected_table?.fields()?.length > 0
				? selected_table.fields()[selected_table.fields().length - 1]
				: null;

			let last_field_idx = last_field ? last_field.idx() : -1;

			let res = await Grape.dialog.open('AddEditTableField', {
				tableField: {
					schema,
					table,
					fields_table_uuid
				},
				last_field_idx: last_field_idx,
				save: false  // return the data
			});

			if (res && res.status === 'OK')
			{
				this.pending_changes.fields_to_create.push(res.tableField);

				// Add to the table's fields for display
				if (selected_table)
				{
					let tempField = new FieldModel(res.tableField);
					tempField.field_uuid('temp_' + Date.now()); // Temporary id
					selected_table.fields.push(tempField);
				}

				// Notify parent 
				this.notify_changes();
			}
		}
	}

	/* edit field */
	async btn_edit_field(field)
	{
		let result = await Grape.dialog.open('AddEditTableField', {
			edit: true,
			tableField: ko.toJS(field),
			save: false // return the data
		});

		if (result && result.status === 'OK')
		{
			this.pending_changes.fields_to_update.push(result.tableField);
			Object.assign(field, result.tableField);
			// Notify parent 
			this.notify_changes();
		}
	}

	/* delete field */
	async btn_delete_field(data)
	{
		let field = ko.toJS(data);
		let confirm = await Grape.alerts.confirm({ type: 'warning', message: `Delete Custom Field <b>${field.display_name}( ${field.name} )</b>? This may result in loss of data.`, cancel_type: 'default', accept_type: 'warning' });

		if (confirm)
		{
			this.pending_changes.fields_to_delete.push(field);

			// Remove from UI
			let filtered_tables = this.filtered_tables() || [];
			for (let table of filtered_tables) {
				let fields = table.fields();
				let fieldIndex = fields.findIndex(f => f.field_uuid() === field.field_uuid);
				if (fieldIndex !== -1)
				{
					fields.splice(fieldIndex, 1);
					table.fields(fields);
					break;
				}
			}

			// Notify parent 
			this.notify_changes();
		}
	}

	/* edit table */
	async btn_edit_table(data, event)
	{
		let res = await Grape.dialog.open('AddEditFieldsTable', { 
			fieldsTable: data, 
			edit: true,
			save: false 
		});

		if (res && res.status === 'OK')
		{
			this.pending_changes.tables_to_update.push(res.fieldsTable);

			// Update UI
			Object.assign(data, res.fieldsTable);

			// Notify parent 
			this.notify_changes();
		}
	}

	/* delete table */
	async btn_delete_field_table(data)
	{
		let table = ko.toJS(data);
		let confirm = await Grape.alerts.confirm({ type: 'warning', title: 'Delete', message: `Delete Custom Fields Table <b>${table.display_name}( ${table.schema}.${table.table} )</b>? This may result in loss of data.`, cancel_type: 'default', accept_type: 'warning' });
		if (confirm)
		{
			this.pending_changes.tables_to_delete.push(table);

			// Remove from UI
			let filtered_tables = this.filtered_tables() || [];
			let tableIndex = filtered_tables.findIndex(t => t.fields_table_uuid() === table.fields_table_uuid);
			if (tableIndex !== -1)
			{
				filtered_tables.splice(tableIndex, 1);
				this.filtered_tables(filtered_tables);
			}

			// Notify parent 
			this.notify_changes();
		}
	}

	async btn_move_table_up(index, idx)
	{
		if (index == 0)
			return;

		let filtered_tables = this.filtered_tables() || [];
		let table_one = filtered_tables[index];
		let table_two = filtered_tables[index - 1];

		// Update UI (swap idx values)
		table_one.idx(table_two.idx());
		table_two.idx(idx);

		// Reorder tables in UI based on new idx
		let sortedTables = [...filtered_tables].sort((a, b) => a.idx() - b.idx());
		this.filtered_tables(sortedTables);

		// Record move with updated idx values
		this.pending_changes.table_moves.push({
			table_one: ko.toJS(table_one),
			table_two: ko.toJS(table_two),
			direction: 'up'
		});

		// Notify parent
		this.notify_changes();
	}

	async btn_move_table_down(index, idx)
	{
		let filtered_tables = this.filtered_tables() || [];
		if (index == filtered_tables.length - 1)
			return;

		let table_one = filtered_tables[index];
		let table_two = filtered_tables[index + 1];

		// Update UI (swap idx values)
		table_one.idx(table_two.idx());
		table_two.idx(idx);

		// Reorder tables in UI based on new idx
		let sortedTables = [...filtered_tables].sort((a, b) => a.idx() - b.idx());
		this.filtered_tables(sortedTables);

		// Record move with updated idx values
		this.pending_changes.table_moves.push({
			table_one: ko.toJS(table_one),
			table_two: ko.toJS(table_two),
			direction: 'down'
		});

		// Notify parent
		this.notify_changes();
	}

	async btn_move_fields_up(t_index, f_index, idx)
	{
		if (f_index == 0)
			return;

		let filtered_tables = this.filtered_tables() || [];

		let field_one = filtered_tables[t_index].fields()[f_index];
		let field_two = filtered_tables[t_index].fields()[f_index - 1];

		// Update UI (swap idx values)
		field_one.idx(field_two.idx());
		field_two.idx(idx);

		// Reorder fields in UI based on new idx
		let fieldsArray = filtered_tables[t_index].fields();
		fieldsArray.sort((a, b) => a.idx() - b.idx());
		filtered_tables[t_index].fields(fieldsArray);

		// Record move with updated idx values
		this.pending_changes.field_moves.push({
			field_one: ko.toJS(field_one),
			field_two: ko.toJS(field_two),
			direction: 'up'
		});

		// Notify parent
		this.notify_changes();
	}

	async btn_move_fields_down(t_index, f_index, idx)
	{
		let filtered_tables = this.filtered_tables() || [];
		if (f_index == filtered_tables[t_index].fields().length - 1)
			return;

		let field_one = filtered_tables[t_index].fields()[f_index];
		let field_two = filtered_tables[t_index].fields()[f_index + 1];

		if (!field_two)
			return;

		// Update UI (swap idx values)
		field_one.idx(field_two.idx());
		field_two.idx(idx);

		// Reorder fields in UI based on new idx
		let fieldsArray = filtered_tables[t_index].fields();
		fieldsArray.sort((a, b) => a.idx() - b.idx());
		filtered_tables[t_index].fields(fieldsArray);

		// Record move with updated idx values
		this.pending_changes.field_moves.push({
			field_one: ko.toJS(field_one),
			field_two: ko.toJS(field_two),
			direction: 'down'
		});

		// Notify parent
		this.notify_changes();
	}

	// Methods for external save handling
	get_pending_changes()
	{
		return this.pending_changes;
	}

	clear_pending_changes()
	{
		this.pending_changes = {
			tables_to_create: [],
			tables_to_update: [],
			tables_to_delete: [],
			fields_to_create: [],
			fields_to_update: [],
			fields_to_delete: [],
			table_moves: [],
			field_moves: []
		};
	}

	has_pending_changes()
	{
		return Object.values(this.pending_changes).some(changes => changes.length > 0);
	}

	// Notify parent component of changes
	notify_changes()
	{
		if (this.on_changes && typeof this.on_changes === 'function')
			this.on_changes(this.pending_changes);
	}

	// apply all pending changes
	async apply_pending_changes()
	{
		try {
			const FieldsTableModel = Grape.dataModel.get('fields.FieldsTable');
			const result = await FieldsTableModel.apply_pending_changes(this.pending_changes);

			if (result.status === 'OK')
			{
				this.clear_pending_changes();
				await this.updateData();
			}

			return result;
		} catch (error) {
			console.error('Error applying pending changes:', error);
			return { status: 'ERROR', message: 'Error saving changes: ' + error.message };
		}
	}
}

export default {
	name: 'ko-field-table-builder',
	viewModel: FieldTableBuilderViewModel,
	module_type: 'ko',
	template: template
};